import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api/axios';
import { formatIndianDate } from '../utils/format';
import ConfirmModal from './ConfirmModal';
import { useToast } from './ToastHost';

const Tasks = ({ user }) => {
  const location = useLocation();
  const toast = useToast();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [projectDevs, setProjectDevs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [total, setTotal] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({
    title: '',
    description: '',
    status: 'Open'
  });
  const [formData, setFormData] = useState({
    project_id: '',
    title: '',
    description: '',
    assigned_to: '',
    due_date: ''
  });

  const [filters, setFilters] = useState({ q: '', status: '', overdue: false });

  const [includeDeleted, setIncludeDeleted] = useState(false);

  const [taskThreadsOpen, setTaskThreadsOpen] = useState({});
  const [taskThreadState, setTaskThreadState] = useState({});
  const [taskReplyDraft, setTaskReplyDraft] = useState({});

  const [actionBusy, setActionBusy] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmBusy, setConfirmBusy] = useState(false);
  const confirmActionRef = useRef(null);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsTask, setDetailsTask] = useState(null);

  const [selectedTaskIds, setSelectedTaskIds] = useState({});
  const [bulkStatus, setBulkStatus] = useState('Open');
  const [bulkBusy, setBulkBusy] = useState(false);

  const initials = (name) => {
    const n = String(name || '').trim();
    if (!n) return 'U';
    const parts = n.split(/\s+/).filter(Boolean);
    const a = parts[0]?.[0] || 'U';
    const b = parts.length > 1 ? parts[parts.length - 1]?.[0] : '';
    return `${a}${b}`.toUpperCase();
  };

  const dayLabel = (d) => new Intl.DateTimeFormat('en-IN', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' }).format(d);

  const threadItems = useCallback((items) => {
    const list = Array.isArray(items) ? items : [];
    const out = [];
    let lastKey = null;
    for (const m of list) {
      const ts = m.created_at ? new Date(m.created_at) : null;
      const key = ts && !Number.isNaN(ts.getTime()) ? `${ts.getFullYear()}-${ts.getMonth()}-${ts.getDate()}` : null;
      if (key && key !== lastKey) {
        out.push({ type: 'divider', key: `d-${key}`, label: dayLabel(ts) });
        lastKey = key;
      }
      out.push({ type: 'msg', key: `m-${m.id}`, msg: m });
    }
    return out;
  }, []);

  const openConfirm = (title, message, action) => {
    confirmActionRef.current = action;
    setConfirmTitle(title);
    setConfirmMessage(message);
    setConfirmOpen(true);
  };

  const projectIdFilter = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const projectId = params.get('projectId');
    return projectId ? String(projectId) : '';
  }, [location.search]);

  const fetchData = useCallback(async (nextPage = page) => {
    try {
      const isDeletedFilter = user.role === 'ADMIN' && filters.status === 'DELETED';
      const tasksPromise = api.get('/api/tasks', {
        params: {
          page: nextPage,
          limit,
          project_id: projectIdFilter || undefined,
          q: filters.q || undefined,
          status: (filters.status && filters.status !== 'DELETED') ? filters.status : undefined,
          overdue: filters.overdue ? true : undefined,
          include_deleted: isDeletedFilter ? true : undefined
        }
      });
      const projectsPromise = api.get('/api/projects', { params: { all: true } });

      // /api/users is accessible to ADMIN + BDM only. For SALES/DEVELOPER we don't need it.
      const usersPromise = (user.role === 'SALES' || user.role === 'DEVELOPER')
        ? Promise.resolve({ data: [] })
        : api.get('/api/users', { params: { all: true } });

      const [tasksRes, projectsRes, usersRes] = await Promise.all([
        tasksPromise,
        projectsPromise,
        usersPromise
      ]);

      const tasksPayload = tasksRes.data;
      let taskList = [];
      if (tasksPayload && typeof tasksPayload === 'object' && Array.isArray(tasksPayload.items)) {
        taskList = tasksPayload.items;
        setTotal(tasksPayload.total || 0);
        setPage(tasksPayload.page || nextPage);
      } else {
        taskList = Array.isArray(tasksPayload) ? tasksPayload : [];
        setTotal(Array.isArray(tasksPayload) ? tasksPayload.length : 0);
        setPage(nextPage);
      }
      // For DELETED filter, show only deleted_at tasks
      if (isDeletedFilter) {
        taskList = taskList.filter((t) => Boolean(t.deleted_at));
        setTotal(taskList.length);
      }
      setTasks(taskList);
      setProjects(projectsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [filters.overdue, filters.q, filters.status, limit, page, projectIdFilter, user.role]);

  const fetchThread = useCallback(async (taskId, nextPage = 1) => {
    try {
      const res = await api.get(`/api/tasks/${taskId}/messages`, { params: { page: nextPage, limit: 10 } });
      const payload = res.data;
      setTaskThreadState((prev) => ({
        ...prev,
        [taskId]: {
          items: payload.items || [],
          total: payload.total || 0,
          page: payload.page || nextPage,
          limit: payload.limit || 10
        }
      }));
    } catch (e) {
      console.error('Error loading task thread:', e);
    }
  }, []);

  const openDetails = useCallback(async (task) => {
    if (!task) return;
    setDetailsTask(task);
    setDetailsOpen(true);
    await fetchThread(task.id, 1);
  }, [fetchThread]);

  const closeDetails = useCallback(() => {
    setDetailsOpen(false);
    setDetailsTask(null);
  }, []);

  useEffect(() => {
    // Threads are collapsed by default. Open explicitly via the "Open" button.
  }, []);

  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  useEffect(() => {
    if (location.state?.openForm && (user.role === 'ADMIN' || user.role === 'BDM')) {
      setShowForm(true);
    }
  }, [location.state, user.role]);

  useEffect(() => {
    if (projectIdFilter && user.role !== 'SALES') {
      setFormData((prev) => ({ ...prev, project_id: projectIdFilter }));
    }
  }, [projectIdFilter, user.role]);

  useEffect(() => {
    fetchData(1);
  }, [fetchData, projectIdFilter]);

  useEffect(() => {
    // Note: Removed the effect that forced status='Open' when projectIdFilter is present.
    // It was causing completed tasks to be hidden when viewing tasks from a project.
  }, []);

  useEffect(() => {
    if (user.role !== 'BDM') return;
    const pid = String(formData.project_id || '').trim();
    if (!pid) {
      setProjectDevs([]);
      return;
    }
    const loadDevs = async () => {
      try {
        const res = await api.get(`/api/projects/${pid}/developers`);
        const arr = Array.isArray(res.data) ? res.data : [];
        setProjectDevs(
          arr
            .map((d) => ({
              id: d.developer_id,
              name: d.name,
              email: d.email,
              role: 'DEVELOPER',
              status: 'ACTIVE'
            }))
            .filter((d) => d.id && d.name)
        );
      } catch (e) {
        setProjectDevs([]);
      }
    };
    loadDevs();
  }, [formData.project_id, user.role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setActionBusy(true);
      await api.post('/api/tasks', formData);
      setShowForm(false);
      setFormData({
        project_id: '',
        title: '',
        description: '',
        assigned_to: '',
        due_date: ''
      });
      fetchData(1);
      toast.success('Task created');
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    } finally {
      setActionBusy(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      await api.put(`/api/tasks/${taskId}`, { status });
      fetchData();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const startEdit = (task) => {
    setEditingId(task.id);
    setEditData({
      title: task.title || '',
      description: task.description || '',
      status: task.status || 'Open'
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const handleEditChange = (e) => {
    setEditData({
      ...editData,
      [e.target.name]: e.target.value
    });
  };

  const saveEdit = async (taskId) => {
    try {
      setActionBusy(true);
      await api.put(`/api/tasks/${taskId}`, editData);
      setEditingId(null);
      fetchData();
      toast.success('Task updated');
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    } finally {
      setActionBusy(false);
    }
  };

  const deleteTask = async (taskId) => {
    openConfirm('Delete task', 'Delete this task?', async () => {
      try {
        setActionBusy(true);
        await api.delete(`/api/tasks/${taskId}`);
        fetchData(1);
        toast.success('Task deleted');
      } catch (error) {
        console.error('Error deleting task:', error);
        toast.error('Failed to delete task');
      } finally {
        setActionBusy(false);
      }
    });
  };

  const restoreTask = async (taskId) => {
    openConfirm('Restore task', 'Restore this task?', async () => {
      try {
        setActionBusy(true);
        await api.put(`/api/tasks/${taskId}/restore`);
        fetchData(1);
        toast.success('Task restored');
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to restore task');
      } finally {
        setActionBusy(false);
      }
    });
  };

  const assignableUsers = useMemo(() => {
    if (user.role === 'BDM') {
      const sales = (users || []).filter((u) => u.role === 'SALES' && u.status === 'ACTIVE');
      const devs = (projectDevs || []).filter((u) => u.role === 'DEVELOPER');
      const seen = new Set();
      const merged = [];
      for (const u of [...sales, ...devs]) {
        const k = String(u.id);
        if (!k || seen.has(k)) continue;
        seen.add(k);
        merged.push(u);
      }
      return merged;
    }
    return (users || []).filter((u) => (u.role === 'SALES' || u.role === 'DEVELOPER') && u.status === 'ACTIVE');
  }, [projectDevs, user.role, users]);

  if (loading) {
    return <div className="text-center p-8">Loading tasks...</div>;
  }

  const totalPages = Math.max(Math.ceil(total / limit) || 1, 1);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const openThread = async (taskId) => {
    const currentlyOpen = Boolean(taskThreadsOpen[taskId]);
    setTaskThreadsOpen((prev) => ({ ...prev, [taskId]: !prev[taskId] }));
    if (!currentlyOpen) {
      await fetchThread(taskId, 1);
    }
  };

  const postReply = async (taskId) => {
    const text = String(taskReplyDraft[taskId] || '').trim();
    if (!text) return;
    try {
      setActionBusy(true);
      await api.post(`/api/tasks/${taskId}/messages`, { message_text: text });
      setTaskReplyDraft((prev) => ({ ...prev, [taskId]: '' }));
      await fetchThread(taskId, 1);
      toast.success('Reply sent');
    } catch (e) {
      console.error('Error posting reply:', e);
      toast.error('Failed to send reply');
    } finally {
      setActionBusy(false);
    }
  };

  const filteredTasks = tasks;

  const selectedCount = Object.values(selectedTaskIds).filter(Boolean).length;
  const canBulk = user.role === 'ADMIN' || user.role === 'BDM';

  const toggleSelectAllOnPage = (checked) => {
    const next = { ...selectedTaskIds };
    for (const t of filteredTasks) {
      next[t.id] = checked;
    }
    setSelectedTaskIds(next);
  };

  const applyBulkStatus = async () => {
    const ids = Object.entries(selectedTaskIds)
      .filter(([, v]) => v)
      .map(([k]) => Number(k))
      .filter(Boolean);
    if (ids.length === 0) return;
    try {
      setBulkBusy(true);
      await api.put('/api/tasks/bulk', { ids, status: bulkStatus });
      toast.success('Bulk update successful');
      setSelectedTaskIds({});
      fetchData(page);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Bulk update failed');
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold">Tasks</h1>

        <div className="flex-1">
          <div className="filters-row">
            <input
              className="glass-input"
              placeholder="Search..."
              value={filters.q}
              onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value }))}
            />
            <select
              className="glass-select"
              value={filters.status}
              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
            >
              <option value="">Status</option>
              <option value="Open">Open</option>
              <option value="InProgress">InProgress</option>
              <option value="Completed">Completed</option>
              {user.role === 'ADMIN' ? (
                <option value="DELETED">Deleted</option>
              ) : null}
            </select>
            <label className="text-sm" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={filters.overdue}
                onChange={(e) => setFilters((prev) => ({ ...prev, overdue: e.target.checked }))}
              />
              Overdue
            </label>
            <button
              type="button"
              className="glass-button"
              onClick={() => setFilters({ q: '', status: projectIdFilter ? 'Open' : '', overdue: false })}
              disabled={actionBusy || confirmBusy}
            >
              Reset
            </button>
          </div>

      {canBulk && filteredTasks.length > 0 ? (
        <div className="glass-card p-4 mb-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <label className="text-sm" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={filteredTasks.length > 0 && filteredTasks.every((t) => Boolean(selectedTaskIds[t.id]))}
                onChange={(e) => toggleSelectAllOnPage(e.target.checked)}
              />
              Select page
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-tertiary">Selected: {selectedCount}</span>
              <select className="glass-select" value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)} disabled={bulkBusy}>
                <option value="Open">Set Open</option>
                <option value="InProgress">Set InProgress</option>
                <option value="Completed">Set Completed</option>
              </select>
              <button
                type="button"
                className="glass-button primary"
                onClick={applyBulkStatus}
                disabled={bulkBusy || selectedCount === 0}
              >
                {bulkBusy ? 'Please wait...' : 'Apply'}
              </button>
              <button
                type="button"
                className="glass-button"
                onClick={() => setSelectedTaskIds({})}
                disabled={bulkBusy || selectedCount === 0}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      ) : null}
        </div>

        {(user.role === 'ADMIN' || user.role === 'BDM') && (
          <button
            onClick={() => setShowForm(true)}
            className="glass-button primary"
            disabled={actionBusy || confirmBusy}
          >
            + New Task
          </button>
        )}
      </div>

      {showForm ? (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal" style={{ width: 'min(860px, 100%)' }}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">Create New Task</h3>
              <button type="button" className="glass-button" onClick={() => setShowForm(false)} disabled={actionBusy || confirmBusy}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Project</label>
                  <select
                    name="project_id"
                    value={formData.project_id}
                    onChange={handleChange}
                    className="glass-select w-full"
                    required
                  >
                    <option value="">Select Project</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.title} - {project.client_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Title</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className="glass-input w-full"
                    required
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="glass-textarea w-full"
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Assign To</label>
                  <select
                    name="assigned_to"
                    value={formData.assigned_to}
                    onChange={handleChange}
                    className="glass-select w-full"
                    required
                  >
                    <option value="">Select User</option>
                    {assignableUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.role})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Due Date</label>
                  <input
                    type="date"
                    name="due_date"
                    value={formData.due_date}
                    onChange={handleChange}
                    className="glass-input w-full"
                  />
                </div>

                <div className="modal-footer col-span-2">
                  <button type="button" className="glass-button" onClick={() => setShowForm(false)} disabled={actionBusy || confirmBusy}>
                    Cancel
                  </button>
                  <button type="submit" className="glass-button primary" disabled={actionBusy || confirmBusy}>
                    {actionBusy ? 'Please wait...' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid gap-4">
        {filteredTasks.map((task) => (
          <div
            key={task.id}
            className="glass-card p-6"
            role="button"
            tabIndex={0}
            onClick={() => openDetails(task)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openDetails(task);
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                {canBulk ? (
                  <label className="text-xs text-tertiary" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(selectedTaskIds[task.id])}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        e.stopPropagation();
                        setSelectedTaskIds((prev) => ({ ...prev, [task.id]: e.target.checked }));
                      }}
                    />
                    Select
                  </label>
                ) : null}
                <h3 className="text-lg font-semibold">{task.title}</h3>
                <p className="text-secondary">Project: {task.project_title}</p>
                {task.assigned_name && (
                  <p className="text-sm text-tertiary">
                    Assigned to: {task.assigned_name}
                    {task.assigned_role ? (
                      <span className={`role-pill role-${String(task.assigned_role || '').toLowerCase()}`} style={{ marginLeft: 8 }}>
                        {task.assigned_role}
                      </span>
                    ) : null}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <span className={`status-badge task-${task.status.toLowerCase()}`}>
                  {task.status}
                </span>
                {task.deleted_at ? (
                  <span className="status-badge" style={{ borderColor: 'rgba(220,53,69,0.26)', background: 'rgba(220,53,69,0.10)' }}>
                    Deleted
                  </span>
                ) : null}
              </div>
            </div>

            {task.description && (
              <div className="mb-4">
                <p className="text-sm text-secondary mb-1">Description:</p>
                <p className="text-sm">{task.description}</p>
              </div>
            )}

            <div className="flex justify-between items-center">
              <div>
                <span className="text-xs text-tertiary">
                  Due: {task.due_date ? formatIndianDate(task.due_date) : 'No due date'}
                </span>
                <span className="text-xs text-tertiary block">
                  Created: {formatIndianDate(task.created_at)}
                </span>
              </div>
              <div className="flex gap-2">
                {(user.role === 'ADMIN' || user.role === 'BDM') && (
                  <>
                    <button
                      type="button"
                      className="glass-button text-sm px-3 py-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        startEdit(task);
                      }}
                      disabled={editingId === task.id || actionBusy || confirmBusy || Boolean(task.deleted_at)}
                    >
                      Edit
                    </button>
                    {task.deleted_at ? (
                      user.role === 'ADMIN' ? (
                        <button
                          type="button"
                          className="glass-button text-sm px-3 py-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            restoreTask(task.id);
                          }}
                          disabled={editingId === task.id || actionBusy || confirmBusy}
                        >
                          Restore
                        </button>
                      ) : null
                    ) : (
                      <button
                        type="button"
                        className="glass-button danger text-sm px-3 py-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteTask(task.id);
                        }}
                        disabled={editingId === task.id || actionBusy || confirmBusy}
                      >
                        Delete
                      </button>
                    )}
                  </>
                )}
                <button
                  type="button"
                  className="glass-button text-sm px-3 py-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    updateTaskStatus(task.id, task.status === 'Completed' ? 'Open' : 'Completed');
                  }}
                  disabled={actionBusy || confirmBusy || Boolean(task.deleted_at)}
                >
                  {task.status === 'Completed' ? 'Reopen' : 'Complete'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editingId && (user.role === 'ADMIN' || user.role === 'BDM') ? (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal" style={{ width: 'min(760px, 100%)' }}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">Edit Task</h3>
              <button type="button" className="glass-button" onClick={cancelEdit} disabled={actionBusy || confirmBusy}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">Title</label>
                  <input type="text" name="title" value={editData.title} onChange={handleEditChange} className="glass-input w-full" required />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea name="description" value={editData.description} onChange={handleEditChange} className="glass-textarea w-full" rows="3" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <select name="status" value={editData.status} onChange={handleEditChange} className="glass-select w-full">
                    <option value="Open">Open</option>
                    <option value="InProgress">InProgress</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="glass-button" onClick={cancelEdit} disabled={actionBusy || confirmBusy}>
                  Cancel
                </button>
                <button type="button" className="glass-button primary" onClick={() => saveEdit(editingId)} disabled={actionBusy || confirmBusy}>
                  {actionBusy ? 'Please wait...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-tertiary">
            Page {page} of {totalPages} ({total} tasks)
          </div>
          <div className="flex gap-2">
            <button type="button" className="glass-button" onClick={() => fetchData(page - 1)} disabled={!canPrev}>
              Prev
            </button>
            <button type="button" className="glass-button" onClick={() => fetchData(page + 1)} disabled={!canNext}>
              Next
            </button>
          </div>
        </div>
      )}

      {filteredTasks.length === 0 && (
        <div className="text-center p-8 glass-card">
          <p className="text-secondary">No tasks found</p>
        </div>
      )}

      <ConfirmModal
        open={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        confirmText="Delete"
        cancelText="Cancel"
        busy={confirmBusy}
        onCancel={() => {
          if (confirmBusy) return;
          setConfirmOpen(false);
        }}
        onConfirm={async () => {
          const action = confirmActionRef.current;
          if (!action) {
            setConfirmOpen(false);
            return;
          }
          try {
            setConfirmBusy(true);
            await action();
            setConfirmOpen(false);
          } finally {
            setConfirmBusy(false);
          }
        }}
      />

      {detailsOpen && detailsTask ? (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={closeDetails}>
          <div className="modal" style={{ width: 'min(980px, 100%)' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">Task Details</h3>
              <button type="button" className="glass-button" onClick={closeDetails} disabled={actionBusy || confirmBusy}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="grid grid-cols-2 gap-4" style={{ marginBottom: 14 }}>
                <div className="col-span-2">
                  <div className="text-xl font-semibold">{detailsTask.title}</div>
                  <div className="text-sm text-secondary">Project: {detailsTask.project_title || `#${detailsTask.project_id}`}</div>
                </div>

                <div>
                  <div className="text-xs text-tertiary">Status</div>
                  <div style={{ marginTop: 6 }}>
                    <span className={`status-badge task-${String(detailsTask.status || '').toLowerCase()}`}>{detailsTask.status}</span>
                  </div>
                </div>

                <div>
                  <div className="text-xs text-tertiary">Due</div>
                  <div className="text-sm" style={{ marginTop: 6 }}>
                    {detailsTask.due_date ? formatIndianDate(detailsTask.due_date) : 'No due date'}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-tertiary">Assigned</div>
                  <div className="text-sm" style={{ marginTop: 6 }}>
                    {detailsTask.assigned_name || '—'}
                    {detailsTask.assigned_role ? (
                      <span className={`role-pill role-${String(detailsTask.assigned_role || '').toLowerCase()}`} style={{ marginLeft: 8 }}>
                        {detailsTask.assigned_role}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-tertiary">Created</div>
                  <div className="text-sm" style={{ marginTop: 6 }}>{formatIndianDate(detailsTask.created_at)}</div>
                </div>

                {detailsTask.description ? (
                  <div className="col-span-2">
                    <div className="text-xs text-tertiary">Description</div>
                    <div className="text-sm" style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{detailsTask.description}</div>
                  </div>
                ) : null}
              </div>

              <div className="text-sm font-semibold mb-2">Discussion</div>
              <div className="chat-panel">
                <div className="chat-thread">
                  {(() => {
                    const items = threadItems(taskThreadState[detailsTask.id]?.items || []);
                    if ((taskThreadState[detailsTask.id]?.items || []).length === 0) {
                      return <div className="text-sm text-secondary">No replies yet</div>;
                    }
                    return (
                      <div className="chat-list">
                        {items.map((it) => {
                          if (it.type === 'divider') {
                            return (
                              <div key={it.key} className="chat-date-divider">
                                <span>{it.label}</span>
                              </div>
                            );
                          }
                          const m = it.msg;
                          const mine = String(m.created_by) === String(user.id);
                          return (
                            <div key={it.key} className={`chat-row ${mine ? 'mine' : 'theirs'}`}>
                              <div className="chat-avatar" aria-hidden="true">{initials(m.created_by_name)}</div>
                              <div className="chat-bubble">
                                <div className="chat-meta">
                                  <div className="chat-name">{m.created_by_name || 'User'}</div>
                                  <span className={`role-pill role-${String(m.created_by_role || '').toLowerCase()}`}>{m.created_by_role}</span>
                                  <span className="chat-time">{formatIndianDate(m.created_at)}</span>
                                </div>
                                <div className="chat-text whitespace-pre-wrap">{m.message_text}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
                <div className="chat-composer" style={{ position: 'static' }}>
                  <div className="flex gap-2">
                    <input
                      className="glass-input w-full"
                      placeholder="Write a reply..."
                      value={taskReplyDraft[detailsTask.id] || ''}
                      onChange={(e) => setTaskReplyDraft((prev) => ({ ...prev, [detailsTask.id]: e.target.value }))}
                    />
                    <button type="button" className="glass-button primary" onClick={() => postReply(detailsTask.id)} disabled={actionBusy || confirmBusy}>
                      Send
                    </button>
                  </div>
                </div>
              </div>

              {taskThreadState[detailsTask.id]?.total > 10 ? (
                <div className="flex items-center justify-between mt-3">
                  <div className="text-xs text-tertiary">
                    Page {taskThreadState[detailsTask.id]?.page || 1}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="glass-button"
                      onClick={() => fetchThread(detailsTask.id, Math.max((taskThreadState[detailsTask.id]?.page || 1) - 1, 1))}
                      disabled={(taskThreadState[detailsTask.id]?.page || 1) <= 1}
                    >
                      Prev
                    </button>
                    <button
                      type="button"
                      className="glass-button"
                      onClick={() => fetchThread(detailsTask.id, (taskThreadState[detailsTask.id]?.page || 1) + 1)}
                      disabled={(taskThreadState[detailsTask.id]?.items || []).length < 10}
                    >
                      Next
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="modal-footer">
                <button type="button" className="glass-button" onClick={closeDetails} disabled={actionBusy || confirmBusy}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Tasks;
